import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, Time, LineData, LineSeries, ISeriesApi } from 'lightweight-charts';
import { TechnicalIndicatorData } from '../../api/metrics';
import { TimezoneMode, formatCrosshairLabel, formatTickMarkLabel } from '../../utils/timezone';
import { getDefaultVisibleCandles } from '../../utils/chartZoom';

interface AtrPanelProps {
  data: TechnicalIndicatorData[];
  loading?: boolean;
  timezone?: TimezoneMode;
  granularityMins?: number;
}

/**
 * ATR (Average True Range) Panel Component
 * Displays volatility indicator - measures market volatility
 * Synchronized with master chart for zoom/pan
 */
const AtrPanel: React.FC<AtrPanelProps> = ({ data, loading, timezone = 'UTC', granularityMins = 60 }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const atrSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current || loading || !data || data.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: 'rgba(148, 163, 184, 0.1)' },
        horzLines: { color: 'rgba(148, 163, 184, 0.2)' },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
        horzLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#475569',
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      rightPriceScale: {
        borderColor: '#475569',
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Add ATR line series
    const atrSeries = chart.addSeries(LineSeries, {
      color: '#FF6B35',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      priceFormat: {
        type: 'price',
        precision: 4,
        minMove: 0.0001,
      },
    });
    atrSeriesRef.current = atrSeries;

    // Convert ATR data to chart format
    const atrData: LineData<Time>[] = data
      .filter(point => point.metricType === 'ATR' && point.period === 14 && point.complete)
      .map(point => ({
        time: (new Date(point.metricTime).getTime() / 1000) as Time,
        value: Number(point.value),
      }))
      .sort((a, b) => (a.time as number) - (b.time as number));

    atrSeries.setData(atrData);

      // Configure chart to auto-scale and fit all data
      chart.priceScale('right').applyOptions({
          scaleMargins: {
              top: 0.1,
              bottom: 0.1,
          },
          autoScale: true,
      });

      // Set visible range based on granularity to match main chart

      const defaultVisibleCandles = getDefaultVisibleCandles(granularityMins);
      const totalCandles = atrData.length;

      if (totalCandles > 0) {
        const from = Math.max(0, totalCandles - defaultVisibleCandles);
        const to = totalCandles - 1;
        chart.timeScale().setVisibleLogicalRange({ from, to });
      }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      atrSeriesRef.current = null;
    };
  }, [loading, data]);

  // Apply timezone changes without recreating chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      timeScale: {
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
    });
  }, [timezone]);


  if (loading) {
    return (
      <div style={{ 
        height: 120, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8',
        backgroundColor: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '4px',
      }}>
        Loading ATR data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: 120, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8',
        backgroundColor: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '4px',
      }}>
        No ATR data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ATR Panel Title */}
      <div style={{
        padding: '8px 12px',
        fontSize: '12px',
        fontWeight: 700,
        color: '#8B5CF6',
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '4px 4px 0 0',
        borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        ATR (14) - Volatility
      </div>

      {/* Chart Container */}
      <div 
        ref={chartContainerRef}
        style={{ 
          width: '100%', 
          flex: 1,
          minHeight: 0,
          position: 'relative',
          backgroundColor: 'rgba(30, 41, 59, 0.3)',
          borderRadius: '0 0 4px 4px',
        }}
      />
    </div>
  );
};

export default AtrPanel;
